import { createContext, useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { authService } from '@services/authService';
import { STORAGE_KEYS } from '@utils/storageKeys';
import analytics from '@services/analyticsService';
import toast from 'react-hot-toast';

export const AuthContext = createContext({});

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [tenant, setTenant] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Carregar usuário do localStorage ao iniciar
  useEffect(() => {
    const loadStoredAuth = async () => {
      try {
        // Suporte a impersonation via query param ?impersonate=TOKEN
        const urlParams = new URLSearchParams(window.location.search);
        const impersonateToken = urlParams.get('impersonate');
        if (impersonateToken) {
          localStorage.setItem(STORAGE_KEYS.TOKEN, impersonateToken);
          localStorage.removeItem(STORAGE_KEYS.USER);
          localStorage.removeItem(STORAGE_KEYS.TENANT);
          // Limpar o param da URL sem recarregar
          urlParams.delete('impersonate');
          const newUrl = window.location.pathname + (urlParams.toString() ? `?${urlParams}` : '');
          window.history.replaceState({}, '', newUrl);
        }

        const token = localStorage.getItem(STORAGE_KEYS.TOKEN);
        const storedUser = localStorage.getItem(STORAGE_KEYS.USER);
        const storedTenant = localStorage.getItem(STORAGE_KEYS.TENANT);

        if (token && storedUser) {
          setUser(JSON.parse(storedUser));
          setTenant(storedTenant ? JSON.parse(storedTenant) : null);
          setIsAuthenticated(true);

          // Validar token com o backend
          try {
            const response = await authService.me();
            setUser(response.user);
            setTenant(response.tenant);
            localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(response.user));
            localStorage.setItem(STORAGE_KEYS.TENANT, JSON.stringify(response.tenant));
          } catch (error) {
            // Token inválido, fazer logout
            console.error('Token inválido:', error);
            await logout();
          }
        } else if (token && !storedUser) {
          // Token de impersonation sem dados armazenados — buscar do backend
          try {
            const response = await authService.me();
            setUser(response.user);
            setTenant(response.tenant);
            localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(response.user));
            localStorage.setItem(STORAGE_KEYS.TENANT, JSON.stringify(response.tenant));
            setIsAuthenticated(true);
          } catch (error) {
            localStorage.removeItem(STORAGE_KEYS.TOKEN);
          }
        }
      } catch (error) {
        console.error('Erro ao carregar auth:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadStoredAuth();
  }, []);

  // Login
  const login = useCallback(async (email, password) => {
    try {
      // Limpar cache do React Query antes de trocar de usuário
      queryClient.clear();

      const response = await authService.login(email, password);

      // Se 2FA está ativado, retornar para o componente de login lidar
      if (response.requires2FA) {
        return { requires2FA: true, tempToken: response.tempToken };
      }

      const { token, user: userData, tenant: tenantData } = response;

      // Salvar no localStorage
      localStorage.setItem(STORAGE_KEYS.TOKEN, token);
      localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(userData));
      localStorage.setItem(STORAGE_KEYS.TENANT, JSON.stringify(tenantData));

      setUser(userData);
      setTenant(tenantData);
      setIsAuthenticated(true);

      toast.success(`Bem-vindo, ${userData.name}!`);

      analytics.identify(userData.userId, { email: userData.email, plan: tenantData?.plan });
      analytics.trackLogin(userData.userId);

      if (userData.role === 'admin') {
        navigate('/admin');
      } else {
        const onboardingComplete = localStorage.getItem(STORAGE_KEYS.ONBOARDING_COMPLETE) === 'true';
        navigate(onboardingComplete ? '/app/dashboard' : '/app/onboarding');
      }

      return { success: true };
    } catch (error) {
      const message = error.response?.data?.error?.message || 'Erro ao fazer login';
      toast.error(message);
      return { success: false, error: message };
    }
  }, [navigate, queryClient]);

  // Login com 2FA (após validar código)
  const loginWith2FA = useCallback(async (tempToken, code) => {
    try {
      const response = await authService.validate2FA(tempToken, code);
      const { token, user: userData, tenant: tenantData } = response;

      localStorage.setItem(STORAGE_KEYS.TOKEN, token);
      localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(userData));
      localStorage.setItem(STORAGE_KEYS.TENANT, JSON.stringify(tenantData));

      setUser(userData);
      setTenant(tenantData);
      setIsAuthenticated(true);

      toast.success(`Bem-vindo, ${userData.name}!`);

      if (userData.role === 'admin') {
        navigate('/admin');
      } else {
        const onboardingComplete = localStorage.getItem(STORAGE_KEYS.ONBOARDING_COMPLETE) === 'true';
        navigate(onboardingComplete ? '/app/dashboard' : '/app/onboarding');
      }

      return { success: true };
    } catch (error) {
      const message = error.response?.data?.error?.message || 'Código inválido';
      toast.error(message);
      return { success: false, error: message };
    }
  }, [navigate, queryClient]);

  // Registro
  const register = useCallback(async (data) => {
    try {
      const response = await authService.register(data);

      // Após o registro, o usuário precisa verificar o e-mail
      // Não salvar token nem autenticar - redirecionar para página de verificação
      analytics.trackSignup(data.email);
      toast.success(response.message || 'Conta criada! Verifique seu e-mail para continuar.');
      navigate(`/verify-email?email=${encodeURIComponent(data.email)}`);

      return { success: true };
    } catch (error) {
      const message = error.response?.data?.error?.message || 'Erro ao criar conta';
      toast.error(message);
      return { success: false, error: message };
    }
  }, [navigate]);

  // Login após verificação de e-mail (chamado pela página VerifyEmail)
  const loginWithToken = useCallback(async ({ token, user: userData, tenant: tenantData }) => {
    // Limpar cache do React Query antes de autenticar
    queryClient.clear();

    localStorage.setItem(STORAGE_KEYS.TOKEN, token);
    localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(userData));
    localStorage.setItem(STORAGE_KEYS.TENANT, JSON.stringify(tenantData));

    setUser(userData);
    setTenant(tenantData);
    setIsAuthenticated(true);

    toast.success(`Bem-vindo, ${userData.name}! Sua conta foi verificada.`);

    // New user after email verification always goes to onboarding
    const onboardingComplete = localStorage.getItem(STORAGE_KEYS.ONBOARDING_COMPLETE) === 'true';
    navigate(onboardingComplete ? '/app/dashboard' : '/app/onboarding');
  }, [navigate, queryClient]);

  // Logout
  const logout = useCallback(async () => {
    try {
      await authService.logout();
    } catch (error) {
      console.error('Erro no logout:', error);
    } finally {
      analytics.reset();

      // Limpar cache do React Query ao sair
      queryClient.clear();

      // Limpar localStorage
      localStorage.removeItem(STORAGE_KEYS.TOKEN);
      localStorage.removeItem(STORAGE_KEYS.USER);
      localStorage.removeItem(STORAGE_KEYS.TENANT);

      setUser(null);
      setTenant(null);
      setIsAuthenticated(false);

      navigate('/login');
    }
  }, [navigate, queryClient]);

  // Atualizar dados do usuário
  const updateUser = useCallback((userData) => {
    setUser((prev) => ({ ...prev, ...userData }));
    localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify({ ...user, ...userData }));
  }, [user]);

  // Atualizar dados do tenant
  const updateTenant = useCallback((tenantData) => {
    setTenant((prev) => ({ ...prev, ...tenantData }));
    localStorage.setItem(STORAGE_KEYS.TENANT, JSON.stringify({ ...tenant, ...tenantData }));
  }, [tenant]);

  // Verificar se tem feature do plano
  const hasFeature = useCallback((feature) => {
    if (!tenant?.plan) return false;

    const planFeatures = {
      free: ['basic'],
      pro: ['basic', 'simulation', 'export', 'investments', 'recurrences', 'installments', 'reports', 'advisor', 'openFinance'],
      family: ['basic', 'simulation', 'export', 'investments', 'recurrences', 'installments', 'reports', 'family', 'shared_accounts', 'advisor', 'openFinance'],
    };

    return planFeatures[tenant.plan]?.includes(feature) || false;
  }, [tenant]);

  // Verificar limite do plano
  const checkLimit = useCallback((resource, current) => {
    if (!tenant?.limits) return true;

    const limit = tenant.limits[resource];
    if (!limit || limit === 999999) return true;

    return current < limit;
  }, [tenant]);

  const value = {
    user,
    tenant,
    isLoading,
    isAuthenticated,
    login,
    loginWith2FA,
    register,
    loginWithToken,
    logout,
    updateUser,
    updateTenant,
    hasFeature,
    checkLimit,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
